import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, CheckCircle, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountSettings = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || '');
  
  const [emailData, setEmailData] = useState({
    new_email: user?.email || '',
    current_password: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false
  });

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.put(
        `${API}/auth/update-email`,
        emailData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Email updated successfully. Please log in again with your new email.');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  // Validate password requirements in real-time
  useEffect(() => {
    const password = passwordData.new_password;
    setPasswordRequirements({
      length: password.length >= 12,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password)
    });
  }, [passwordData.new_password]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    // Validate all password requirements
    if (!passwordRequirements.length) {
      setError('Password must be at least 12 characters long');
      return;
    }
    if (!passwordRequirements.lowercase) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!passwordRequirements.uppercase) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!passwordRequirements.number) {
      setError('Password must contain at least one number');
      return;
    }
    if (!passwordRequirements.special) {
      setError('Password must contain at least one special character (@$!%*?&)');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/auth/update-password`,
        passwordData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Password updated successfully. Please log in again.');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        
        // Update profile photo via API
        await axios.put(
          `${API}/users/update-photo`,
          { profile_photo: base64String },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        // Update local state with the photo for immediate display
        setProfilePhoto(base64String);
        
        // Update user context - the context already handles localStorage quota
        updateUser({ profile_photo: base64String });
        
        toast.success('Profile photo updated successfully');
        
        // Force a re-render by updating state again after a brief delay
        setTimeout(() => {
          setProfilePhoto(base64String);
        }, 100);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to update profile photo');
    } finally {
      setLoading(false);
    }
  };

  // Warn user about unsaved changes in forms
  useEffect(() => {
    const hasEmailChanges = emailData.new_email !== user?.email || emailData.current_password !== '';
    const hasPasswordChanges = passwordData.current_password !== '' || passwordData.new_password !== '' || passwordData.confirm_password !== '';
    const hasUnsavedChanges = hasEmailChanges || hasPasswordChanges;
    
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [emailData, passwordData, user]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center gap-6">
          <div className="relative">
            <button
              onClick={() => setShowAccountInfo(!showAccountInfo)}
              className="relative group cursor-pointer focus:outline-none"
              title="Click to view account information"
            >
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg group-hover:shadow-xl transition-shadow"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center border-4 border-white shadow-lg group-hover:shadow-xl transition-shadow">
                  <span className="text-3xl font-bold text-white">
                    {user?.first_name?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-cyan-700 rounded-full flex items-center justify-center border-2 border-white group-hover:bg-cyan-600 transition-colors">
                {showAccountInfo ? (
                  <ChevronUp size={14} className="text-white" />
                ) : (
                  <ChevronDown size={14} className="text-white" />
                )}
              </div>
            </button>
            
            {/* Photo Upload Button */}
            <input
              type="file"
              id="photo-upload"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <label
              htmlFor="photo-upload"
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded-full cursor-pointer shadow-md transition-colors whitespace-nowrap"
            >
              Change Photo
            </label>
          </div>
          
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-1">Account Settings</h1>
            <p className="text-lg text-slate-600">Manage your profile and credentials</p>
          </div>
        </div>

        {/* Temporary Password Alert */}
        {user?.has_temporary_password && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-300">
            <AlertCircle className="h-4 w-4 text-yellow-700" />
            <AlertDescription className="text-yellow-900">
              <strong>Security Notice:</strong> You are using a temporary password. Please change your password below for security.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          {/* Account Information - Collapsible */}
          {showAccountInfo && (
            <div className="card animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Your Information</h2>
                <button
                  onClick={() => setShowAccountInfo(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  title="Hide information"
                >
                  <ChevronUp size={20} />
                </button>
              </div>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Name</p>
                  <p className="text-slate-900 font-medium">
                    {user?.first_name} {user?.middle_name} {user?.last_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">User ID</p>
                  <p className="text-slate-900 font-mono">
                    {user?.role === 'provider' && `D-${user?.id.replace('user_provider_', '').replace('provider_', '').padStart(5, '0')}`}
                    {user?.role === 'admin' && `A-${user?.id.replace('user_admin_', '').replace('admin_', '').padStart(5, '0')}`}
                    {user?.role === 'patient' && `P-${user?.id.replace('user_patient_', '').replace('patient_', '').padStart(5, '0')}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Current Email</p>
                  <p className="text-slate-900">{user?.email}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Role</p>
                  <p className="text-slate-900 capitalize">{user?.role}</p>
                </div>
                
                {/* Provider-specific information */}
                {user?.role === 'provider' && (
                  <>
                    {user?.provider_role && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Specialty</p>
                        <p className="text-slate-900">{user.provider_role}</p>
                      </div>
                    )}
                    {user?.clinic_hours && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Clinic Hours</p>
                        <p className="text-slate-900">{user.clinic_hours}</p>
                      </div>
                    )}
                    {user?.phone && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Contact Phone</p>
                        <p className="text-slate-900">{user.phone}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Provider Information Update - Providers Only */}
          {user?.role === 'provider' && (
            <div className="card bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Update Provider Information</h2>
              <p className="text-sm text-slate-600 mb-4">
                Keep your professional details up to date so patients can reach you easily.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="provider-specialty">Your Specialty/Role</Label>
                  <Input
                    id="provider-specialty"
                    type="text"
                    defaultValue={user?.provider_role || ''}
                    placeholder="e.g., Primary Care Physician, Cardiologist"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="clinic-hours">Clinic Hours</Label>
                  <Input
                    id="clinic-hours"
                    type="text"
                    defaultValue={user?.clinic_hours || ''}
                    placeholder="e.g., Mon-Fri, 9AM-5PM"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="contact-phone">Contact Phone (for patients)</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    defaultValue={user?.phone || ''}
                    placeholder="+639171234567"
                    className="mt-1"
                  />
                </div>
                
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                  <p className="text-xs text-blue-900">
                    <strong>Note:</strong> These details will be visible to your assigned patients in their Care Team section.
                  </p>
                </div>
                
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Save Provider Information
                </Button>
              </div>
            </div>
          )}

          {/* Change Email */}
          <div className="card">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Change Email Address</h2>
            <form onSubmit={handleEmailChange} className="space-y-4">
              <div>
                <Label htmlFor="new_email">New Email Address</Label>
                <Input
                  id="new_email"
                  type="email"
                  value={emailData.new_email}
                  onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email_password">Current Password (to confirm)</Label>
                <Input
                  id="email_password"
                  type="password"
                  value={emailData.current_password}
                  onChange={(e) => setEmailData({ ...emailData, current_password: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                <Save size={16} className="mr-2" />
                {loading ? 'Updating...' : 'Update Email'}
              </Button>
            </form>
          </div>

          {/* Change Password */}
          <div className="card">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  className="mt-1"
                />
                {/* Password Requirements Checklist */}
                <div className="mt-3 space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Password must contain:</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      {passwordRequirements.length ? (
                        <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      <span className={passwordRequirements.length ? "text-green-700 font-medium" : "text-slate-600"}>
                        At least 12 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {passwordRequirements.lowercase ? (
                        <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      <span className={passwordRequirements.lowercase ? "text-green-700 font-medium" : "text-slate-600"}>
                        One lowercase letter (a-z)
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {passwordRequirements.uppercase ? (
                        <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      <span className={passwordRequirements.uppercase ? "text-green-700 font-medium" : "text-slate-600"}>
                        One uppercase letter (A-Z)
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {passwordRequirements.number ? (
                        <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      <span className={passwordRequirements.number ? "text-green-700 font-medium" : "text-slate-600"}>
                        One number (0-9)
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {passwordRequirements.special ? (
                        <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                      ) : (
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0" />
                      )}
                      <span className={passwordRequirements.special ? "text-green-700 font-medium" : "text-slate-600"}>
                        One special character (@$!%*?&)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                <Save size={16} className="mr-2" />
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </div>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> After changing your email or password, you will be logged out and need to sign in again with your new credentials.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;